/* global axios */
import ApiClient from './ApiClient';

class ScheduledMessagesApi extends ApiClient {
  constructor() {
    super('scheduled_messages', { accountScoped: true });
  }

  /**
   * Helper para construir FormData com chaves aninhadas para Rails strong params
   * Ex: { recurrence_config: { days: [1,2] } } => scheduled_message[recurrence_config][days][]
   */
  _buildNestedFormData(data, files, paramName) {
    const formData = new FormData();

    const appendNested = (obj, prefix) => {
      Object.entries(obj).forEach(([key, value]) => {
        const fieldName = prefix ? `${prefix}[${key}]` : key;

        if (value === null || value === undefined) {
          // Ignorar valores nulos
          return;
        }

        if (Array.isArray(value)) {
          // Arrays: usar [] no nome do campo
          value.forEach(item => {
            if (typeof item === 'object' && item !== null) {
              // Array de objetos (ex: recipients)
              appendNested(item, `${fieldName}[]`);
            } else {
              formData.append(`${fieldName}[]`, item);
            }
          });
        } else if (typeof value === 'object' && !(value instanceof File)) {
          // Objetos aninhados: recursão
          appendNested(value, fieldName);
        } else {
          // Valores simples
          formData.append(fieldName, value);
        }
      });
    };

    // Adicionar dados do payload com o prefixo do param name
    appendNested(data, paramName);

    // Adicionar arquivos
    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append(`${paramName}[attachments][]`, file);
      });
    }

    return formData;
  }

  // Conversation-scoped methods
  getForConversation(conversationId) {
    return axios.get(
      `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages`
    );
  }

  createForConversation(conversationId, data, files = []) {
    // Se houver arquivos, usar FormData
    if (files && files.length > 0) {
      const formData = this._buildNestedFormData(
        data,
        files,
        'scheduled_message'
      );
      return axios.post(
        `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages`,
        formData
      );
    }

    // Sem arquivos, manter comportamento atual (JSON)
    return axios.post(
      `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages`,
      { scheduled_message: data }
    );
  }

  updateForConversation(conversationId, messageId, data, files = []) {
    // Se houver arquivos, usar FormData
    if (files && files.length > 0) {
      const formData = this._buildNestedFormData(
        data,
        files,
        'scheduled_message'
      );
      return axios.patch(
        `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages/${messageId}`,
        formData
      );
    }

    return axios.patch(
      `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages/${messageId}`,
      { scheduled_message: data }
    );
  }

  deleteForConversation(conversationId, messageId) {
    return axios.delete(
      `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages/${messageId}`
    );
  }

  showForConversation(conversationId, messageId) {
    return axios.get(
      `${this.baseUrl()}/conversations/${conversationId}/scheduled_messages/${messageId}`
    );
  }

  // Broadcast-scoped methods
  getBroadcasts() {
    return axios.get(`${this.baseUrl()}/scheduled_broadcasts`);
  }

  createBroadcast(data, files = []) {
    // Se houver arquivos, usar FormData
    if (files && files.length > 0) {
      const formData = this._buildNestedFormData(
        data,
        files,
        'scheduled_broadcast'
      );
      return axios.post(`${this.baseUrl()}/scheduled_broadcasts`, formData);
    }

    // Sem arquivos, manter comportamento atual (JSON)
    return axios.post(`${this.baseUrl()}/scheduled_broadcasts`, {
      scheduled_broadcast: data,
    });
  }

  updateBroadcast(messageId, data, files = []) {
    // Se houver arquivos, usar FormData
    if (files && files.length > 0) {
      const formData = this._buildNestedFormData(
        data,
        files,
        'scheduled_broadcast'
      );
      return axios.patch(
        `${this.baseUrl()}/scheduled_broadcasts/${messageId}`,
        formData
      );
    }

    return axios.patch(`${this.baseUrl()}/scheduled_broadcasts/${messageId}`, {
      scheduled_broadcast: data,
    });
  }

  deleteBroadcast(messageId) {
    return axios.delete(`${this.baseUrl()}/scheduled_broadcasts/${messageId}`);
  }

  showBroadcast(messageId) {
    return axios.get(`${this.baseUrl()}/scheduled_broadcasts/${messageId}`);
  }
}

export default new ScheduledMessagesApi();
